import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';

import Iconify from 'src/components/iconify';

const APP_STORE_URL = 'https://apps.apple.com/app/metamask/id1438144202';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=io.metamask';
const METAMASK_DOWNLOAD_URL = 'https://metamask.io/download/';

const GREEN_GRADIENT = 'linear-gradient(90deg, #E9E637 0%, #A6DC2E 48%, #6FCF2C 100%)';

OpenMetaMaskDialog.propTypes = {
  open: PropTypes.bool,
  link: PropTypes.string,
  onCancel: PropTypes.func,
};

function getDownloadLink() {
  if (typeof navigator === 'undefined') return METAMASK_DOWNLOAD_URL;

  const userAgent = navigator.userAgent || '';

  if (/iphone|ipad|ipod/i.test(userAgent)) return APP_STORE_URL;
  if (/android/i.test(userAgent)) return PLAY_STORE_URL;

  return METAMASK_DOWNLOAD_URL;
}

function getPaperShadow(mode) {
  if (mode === 'light') return '0 24px 64px rgba(16, 24, 40, 0.18)';
  return '0 24px 64px rgba(0, 0, 0, 0.45)';
}

// Phone browsers only, and NOT used for connecting (that stays inside the
// "Connect with MetaMask" dialog). Shown when a later approval (Send, Switch
// network) needs MetaMask and the browser blocked the automatic switch to the
// app; a tap on this link is always allowed.
export default function OpenMetaMaskDialog({ open, link, onCancel }) {
  const theme = useTheme();

  return (
    <Dialog
      open={Boolean(open && link)}
      onClose={onCancel}
      disableEnforceFocus
      disableRestoreFocus
      disableScrollLock
      fullWidth
      maxWidth="xs"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.common.black, 0.52),
            backdropFilter: 'blur(4px)',
          },
        },
        paper: {
          sx: {
            width: 'clamp(320px, 92vw, 420px)',
            boxSizing: 'border-box',
            maxWidth: 420,
            maxHeight: '90vh',
            overflow: 'hidden',
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: getPaperShadow(theme.palette.mode),
            backgroundColor: theme.palette.background.paper,
            backgroundImage: `linear-gradient(180deg, ${alpha(
              theme.palette.success.main,
              0.07
            )} 0%, transparent 28%)`,
          },
        },
      }}
    >
      <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.25 }, pb: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.success.main, 0.28)}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Icon icon="solar:wallet-bold" width={20} color={theme.palette.success.main} />
            </Box>

            <Box sx={{ pr: 1 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, mt: 0.25 }}
              >
                Continue in MetaMask
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75, lineHeight: 1.65 }}
              >
                Tap Open MetaMask to approve the request, then come back to this page.
              </Typography>
            </Box>
          </Stack>

          <IconButton
            onClick={onCancel}
            aria-label="Close MetaMask prompt"
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.default, 0.72),
              flexShrink: 0,
            }}
          >
            <Iconify icon="mingcute:close-line" width={18} />
          </IconButton>
        </Stack>
      </Box>

      <Divider />

      <DialogContent
        sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: { xs: 2.5, sm: 3 }, overflowY: 'auto' }}
      >
        <Stack spacing={1.25}>
          <Button
            fullWidth
            component="a"
            href={link || undefined}
            rel="noopener"
            variant="contained"
            size="large"
            startIcon={<Iconify icon="solar:arrow-right-up-bold" width={18} />}
            sx={{
              height: 48,
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '0.9rem',
              letterSpacing: '-0.01em',
              boxShadow: 'none',
              background: GREEN_GRADIENT,
              color: '#0A0B08',
              '&:hover': {
                background: GREEN_GRADIENT,
                filter: 'brightness(1.05)',
                boxShadow: 'none',
              },
            }}
          >
            Open MetaMask
          </Button>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={onCancel}
            sx={{
              height: 48,
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.9rem',
              letterSpacing: '-0.01em',
              boxShadow: 'none',
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              bgcolor: alpha(theme.palette.background.default, 0.6),
              '&:hover': {
                borderColor: alpha(theme.palette.text.primary, 0.3),
                bgcolor: alpha(theme.palette.action.hover, 0.6),
                boxShadow: 'none',
              },
            }}
          >
            Cancel
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center', pt: 0.5 }}
          >
            {"Don't have MetaMask? "}
            <Box
              component="a"
              href={getDownloadLink()}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}
            >
              Install it
            </Box>
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
